import * as React from "react";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  Text,
  Tabs,
  SegmentedControl,
  Modal,
  ActionIcon,
  Flex,
  Stack,
  Divider,
  TextInput,
} from "@mantine/core";
import { CTextInput, CMultiSelect, CSelect, CTextarea } from "@supportinitiative/react-hook-form-mantine";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { object, string, number, date, array, setLocale } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { inlineQueryOperators, operators } from "../../../UseCaseUtils";
import { getId } from "../../../../utilities";
import { useLoaders } from "src/hooks";
import { UseCaseCVMContext } from "../../../UseCaseCVMContext";
import { UAction, UBulkDataLoadActionType, UseCaseType } from "@supportinitiative/common";

setLocale({
  mixed: {
    required: "Required",
    notType: "Invalid Value",
  },
});

function getDatasetsLOV(recon?: UseCaseType["definition"]["recons"][number]) {
  if (recon) {
    if (recon.definition.datasets) {
      const datasets = recon.definition.datasets.map((d) => {
        return {
          value: d.id,
          label: `${d.name} [ ${d.id} ]`,
        };
      });
      datasets.push({
        value: "__ALL",
        label: `__ALL`,
      });
      return datasets;
    } else {
      return [];
    }
  } else {
    return [];
  }
}

const BulkDataLoadReconActionSchema = object({
  id: string().required(),
  name: string().required(),
  type: string().required().oneOf(["BULK_DATALOAD"]),
  description: string().nullable(),
  definition: object({
    datasets: array().of(string().required()).min(1),
    type: string().required().oneOf(["CUSTOM", "STAGE"]),
    config: string().when("type", {
      is: "CUSTOM",
      then: (schema) => schema.required(),
    }),
  }),
});

function BulkDataLoadRecon({
  recon,
  save,
  cancel,
  action,
}: {
  cancel: () => void;
  save: (values: any) => void;
  action: any;
  recon?: UseCaseType["definition"]["recons"][number];
}) {
  const { state: useCaseFormState } = React.useContext(UseCaseCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UAction<UBulkDataLoadActionType>>({
    defaultValues: {
      ...action,
      definition: {
        ...action.definition,
        type: action.definition.type || "DATASET",
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(BulkDataLoadReconActionSchema),
  });

  const { data: loaders } = useLoaders(useCaseFormState.entityId);

  return (
    <Modal centered size="xl" opened={true} onClose={cancel} title={<Title order={4}>Data Load</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          console.log("inside child");
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput name="id" control={control} label="ID" required showErrorText={false} disabled={true} />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              name="description"
              minLength={5}
              control={control}
              label="Description"
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Divider my="xs" label="Dataset" />
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CMultiSelect
              name={`definition.datasets`}
              control={control}
              label={"Datasets"}
              data={getDatasetsLOV(recon)}
              showErrorText={false}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name={`definition.type`}
              control={control}
              label={"Type"}
              data={[
                {
                  value: "STAGE",
                  label: "Stage",
                },
                {
                  value: "CUSTOM",
                  label: "Custom",
                },
              ]}
              showErrorText={false}
              required
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name="definition.loaderId"
              label="Loader"
              placeholder="Select Loader"
              control={control}
              data={
                loaders && loaders.length > 0
                  ? loaders.map((loader: any) => {
                      return { label: loader.name, value: loader.id };
                    })
                  : []
              }
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              minLength={5}
              name="definition.config"
              label="Config"
              placeholder="Config"
              control={control}
              disabled={useCaseFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                useCaseFormState.actionOptions.disableAll
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await BulkDataLoadReconActionSchema.validate(values, {
                          abortEarly: false,
                        });
                        console.log("values", values);
                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}

export default BulkDataLoadRecon;
